import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Table, TableBody, TableCell, TableHead, TableRow, Grid, FormHelperText } from '@mui/material';
import { Button } from '@totalsoft/rocket-ui';
import CollapseOrBlankCard from 'components/common/CollapseOrBlankCard';
import { withTranslation } from 'react-i18next';
import People from '@mui/icons-material/People';
import OfferPartnerShareholderRowComponent from './OfferPartnerShareholderRowComponent';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import OfferPartnerShareholdersSummary from './OfferPartnerShareholdersSummary';
import CardNoResultsWrapper from 'components/layout/CardNoResultsWrapper';
import { GridActionsWrapper } from 'components/common/CommonStyles';
import { GlobalRightsContext } from 'providers/GlobalRightsContext';
import WrappedFakeText from 'components/common/WrappedFakeText';

export const OfferPartnerShareholderComponent = ({
  disabled,
  saving,
  shareholderList,
  loadPartners,
  onPropertyChange,
  onClearShareholder,
  addShareholder,
  saveShareholders,
  deleteShareholder,
  shareholderCardExpanded,
  onShareholderCardExpanded,
  loadPartnerDetails,
  t,
  partnerId,
  saveDisabled,
  validationShareholderSelection,
  shareholdersLoaded,
  openMasterPartner,
  openDocumentPartner,
  masterPartnerApp,
  isAssociatedPartner,
  partnerTypes,
  documentId,
  siteId
}) => {
  const { shareholderPhone: viewShareholderPhone } = useContext(GlobalRightsContext);

  return (
    <>
      <CollapseOrBlankCard
        id='shareholderCard'
        icon={People}
        title={t('OfferEdit.Client.Shareholder.Title')}
        expanded={shareholderCardExpanded}
        onToggle={onShareholderCardExpanded}
        subheader={<OfferPartnerShareholdersSummary documentId={documentId} siteId={siteId} partnerId={partnerId} />}
        hideSubheaderOnExpand
        canExpand={partnerId != null}
      >
        {!shareholdersLoaded && shareholderCardExpanded ? (
          <WrappedFakeText />
        ) : (
          <Grid container>
            <Grid item xs={12}>
              <CardNoResultsWrapper content={shareholderList} overflow='auto'>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>{t('OfferEdit.Client.Shareholder.UniqueID')}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{t('OfferEdit.Client.Shareholder.PartnerType')}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{t('OfferEdit.Client.Shareholder.Name')}</strong>
                      </TableCell>
                      {viewShareholderPhone && (
                        <TableCell>
                          <strong>{t('OfferEdit.Client.Shareholder.Phone')}</strong>
                        </TableCell>
                      )}
                      <TableCell>
                        <strong>{t('OfferEdit.Client.Shareholder.Percent')}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{t('OfferEdit.Client.Shareholder.IsRealBeneficiary')}</strong>
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shareholderList.map((document, idx) => {
                      return (
                        <OfferPartnerShareholderRowComponent
                          key={idx}
                          idx={idx}
                          disabled={disabled}
                          shareholder={document}
                          loadPartners={loadPartners}
                          deleteShareholder={() => deleteShareholder(document)}
                          onPropertyChange={onPropertyChange(idx)}
                          onClearShareholder={() => onClearShareholder(idx)}
                          loadPartnerDetails={loadPartnerDetails}
                          validationShareholderSelection={validationShareholderSelection}
                          openMasterPartner={openMasterPartner}
                          openDocumentPartner={openDocumentPartner}
                          masterPartnerApp={masterPartnerApp}
                          isAssociatedPartner={isAssociatedPartner}
                          partnerTypes={partnerTypes}
                          viewShareholderPhone={viewShareholderPhone}
                        />
                      );
                    })}
                  </TableBody>
                </Table>
              </CardNoResultsWrapper>
              {!isValid(validationShareholderSelection) && shareholderList && shareholderList.length !== 0 && (
                <FormHelperText error={true} id='name-error-text'>
                  {getErrors(validationShareholderSelection)}
                </FormHelperText>
              )}
              <GridActionsWrapper mt={2}>
                <Button id='shareholder_addBtn' disabled={disabled} onClick={() => addShareholder()}>
                  {t('Add')}
                </Button>
                <Button
                  id='shareholder_saveBtn'
                  onClick={() => saveShareholders(partnerId, shareholderList)}
                  disabled={disabled || saveDisabled || !isValid(validationShareholderSelection)}
                >
                  {saving ? t('Saving') : t('Save')}
                </Button>
              </GridActionsWrapper>
            </Grid>
          </Grid>
        )}
      </CollapseOrBlankCard>
    </>
  );
};

OfferPartnerShareholderComponent.propTypes = {
  disabled: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  shareholderList: PropTypes.array.isRequired,
  loadPartners: PropTypes.func.isRequired,
  deleteShareholder: PropTypes.func.isRequired,
  addShareholder: PropTypes.func.isRequired,
  saveShareholders: PropTypes.func.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onClearShareholder: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  partnerId: PropTypes.number,
  shareholderCardExpanded: PropTypes.bool,
  onShareholderCardExpanded: PropTypes.func,
  loadPartnerDetails: PropTypes.func.isRequired,
  saveDisabled: PropTypes.bool,
  totalPercentageValid: PropTypes.bool,
  validationShareholderSelection: PropTypes.object,
  shareholdersLoaded: PropTypes.bool,
  openMasterPartner: PropTypes.func,
  openDocumentPartner: PropTypes.func,
  masterPartnerApp: PropTypes.bool,
  isAssociatedPartner: PropTypes.bool.isRequired,
  partnerTypes: PropTypes.array.isRequired,
  documentId: PropTypes.number,
  siteId: PropTypes.number
};

export default withTranslation()(OfferPartnerShareholderComponent);
